import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const AboutPage = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const values = t("values.items", { returnObjects: true }) as { icon: string; title: string; desc: string }[];
  const areas = t("area.areas", { returnObjects: true }) as string[];
  const paymentMethods = t("payment.methods", { returnObjects: true }) as string[];

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full h-[460px] md:h-[540px] overflow-hidden">
          <img src="https://readdy.ai/api/search-image?query=family%20owned%20cleaning%20business%20professional%20team%20smiling%20friendly%20warm%20Florida%20home%20service%20trustworthy%20reliable%20people&width=1920&height=600&seq=about1&orientation=landscape" alt="About Adry & Mich" className="absolute inset-0 w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-brand-dark/70"></div>
          <div className="absolute inset-0 flex items-center justify-center pt-20">
            <div className="text-center px-4">
              <div className="flex items-center justify-center gap-2 text-white/60 text-sm font-body mb-4">
                <Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-white">{lang === "en" ? "About Us" : "Nosotros"}</span>
              </div>
              <span className="inline-block bg-brand-green/20 border border-brand-green/40 text-green-300 px-4 py-1.5 rounded-full text-sm font-body mb-4">{t("hero.badge")}</span>
              <h1 className="font-heading font-black text-white text-4xl md:text-6xl mb-4">{t("hero.title")}</h1>
              <p className="font-body text-white/70 text-base md:text-lg max-w-2xl mx-auto">{t("hero.subtitle")}</p>
            </div>
          </div>
        </section>

        {/* Story */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-dark mb-6">{t("story.title")}</h2>
                <p className="font-body text-gray-600 text-base leading-relaxed mb-4">{t("story.p1")}</p>
                <p className="font-body text-gray-600 text-base leading-relaxed mb-4">{t("story.p2")}</p>
                <p className="font-body text-gray-600 text-base leading-relaxed mb-8">{t("story.p3")}</p>
              </div>
              <div className="relative">
                <img src="https://readdy.ai/api/search-image?query=professional%20cleaning%20service%20team%20working%20together%20bright%20Florida%20home%20interior%20friendly%20family%20business%20trustworthy&width=700&height=600&seq=about2&orientation=portrait" alt="Our team" className="w-full h-[420px] object-cover object-top rounded-2xl" />
                <div className="absolute -bottom-4 -left-4 bg-brand-green text-white rounded-2xl p-4 shadow-lg">
                  <p className="font-heading font-bold text-lg">Adry &amp; Mich</p>
                  <p className="font-body text-green-100 text-xs">{lang === "en" ? "Family-Owned Business" : "Negocio Familiar"}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-16 bg-brand-beige">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-dark text-center mb-10">{t("values.title")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {values.map((v, i) => (
                <div key={i} className="bg-white rounded-2xl p-8 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-brand-green/10 rounded-2xl mx-auto mb-4">
                    <i className={`${v.icon} text-brand-green text-2xl`}></i>
                  </div>
                  <h3 className="font-heading font-bold text-brand-dark text-lg mb-3">{v.title}</h3>
                  <p className="font-body text-gray-500 text-sm leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Schedule + Payment */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-brand-beige rounded-2xl p-8">
                <div className="w-12 h-12 flex items-center justify-center bg-brand-green/10 rounded-xl mb-4">
                  <i className="ri-calendar-line text-brand-green text-xl"></i>
                </div>
                <h3 className="font-heading font-bold text-brand-dark text-xl mb-3">{t("schedule.title")}</h3>
                <p className="font-body text-gray-700 font-medium">{t("schedule.days")}</p>
                <p className="font-body text-brand-green font-bold text-lg">{t("schedule.hours")}</p>
                <p className="font-body text-gray-500 text-sm mt-2 italic">{t("schedule.note")}</p>
              </div>
              <div className="bg-brand-beige rounded-2xl p-8">
                <div className="w-12 h-12 flex items-center justify-center bg-brand-navy/10 rounded-xl mb-4">
                  <i className="ri-bank-card-line text-brand-navy text-xl"></i>
                </div>
                <h3 className="font-heading font-bold text-brand-dark text-xl mb-3">{t("payment.title")}</h3>
                <div className="flex flex-wrap gap-2">
                  {paymentMethods.map((m, i) => (
                    <span key={i} className="bg-white border border-gray-200 text-brand-dark px-4 py-2 rounded-full text-sm font-body font-medium">{m}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Service Area */}
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-brand-dark mb-3">{t("area.title")}</h2>
            <p className="font-body text-gray-500 text-base mb-6">{t("area.subtitle")}</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="flex flex-wrap gap-2">
                {areas.map((a, i) => (
                  <span key={i} className="flex items-center gap-2 bg-brand-beige px-4 py-2 rounded-full text-sm font-body text-brand-dark">
                    <i className="ri-map-pin-line text-brand-green text-xs"></i>{a}
                  </span>
                ))}
              </div>
              <div className="rounded-2xl overflow-hidden h-64">
                <iframe
                  title="Adry & Mich Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3564.6433440494666!2d-80.21460642477173!3d26.691886876779407!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88d92f1fb7365f2d%3A0x12c37bf3f18362b2!2s10639%20Misty%20Ln%2C%20Royal%20Palm%20Beach%2C%20FL%2033411%2C%20USA!5e0!3m2!1sen!2sco!4v1775489619297!5m2!1sen!2sco"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-14 bg-brand-green">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <h2 className="font-heading font-bold text-white text-2xl md:text-3xl mb-3">
              {lang === "en" ? "Ready to work with us?" : "¿Listo para trabajar con nosotros?"}
            </h2>
            <p className="font-body text-green-100 text-base mb-6">
              {lang === "en" ? "Get your free quote today — we respond fast!" : "Obtén tu cotización gratis hoy — ¡respondemos rápido!"}
            </p>
            <a href="https://wa.me/15612626118" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white text-brand-green hover:bg-green-50 px-7 py-3.5 rounded-full font-heading font-semibold text-sm transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-whatsapp-line text-lg"></i>
              {lang === "en" ? "Contact Us on WhatsApp" : "Contáctanos por WhatsApp"}
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default AboutPage;
